import { useEffect, useRef } from "react";

/**
 * Add `reveal` + variant classes to elements; this hook attaches an
 * IntersectionObserver to the ref and toggles `is-visible` on entry.
 * Default: triggers once.
 */
export function useReveal(options = {}) {
  const { once = true, threshold = 0.18, rootMargin = "0px 0px -10% 0px" } = options;
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const targets = Array.from(node.querySelectorAll(".reveal"));
    if (node.classList.contains("reveal")) targets.unshift(node);
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            entry.target.classList.remove("is-visible");
          }
        });
      },
      { threshold, rootMargin }
    );

    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [once, threshold, rootMargin]);

  return ref;
}

/** Counter count-up — triggers once when the element enters. */
export function useCountUp(targetRef, end, duration = 1800) {
  useEffect(() => {
    const node = targetRef.current;
    if (!node) return;
    let started = false;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            const startTime = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - startTime) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              node.textContent = Math.round(end * eased).toLocaleString();
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.unobserve(node);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [targetRef, end, duration]);
}
